/**
 * BalanceCard Component
 * Display net balance, income, and expenses with dynamic colors
 * Now supports FX comparison currency display
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';
import { useFxConversion } from '@/hooks/useFxConversion';

export default function BalanceCard({ income = 0, expenses = 0 }) {
  const { currency: userCurrency, comparisonCurrency } = useAuth();
  const currency = userCurrency || 'USD';
  const balance = useMemo(() => income - expenses, [income, expenses]);
  const isPositive = balance >= 0;
  const [animatedBalance, setAnimatedBalance] = useState(0);

  // FX conversion for comparison currency
  const shouldShowFx = comparisonCurrency && comparisonCurrency !== currency;
  const { convert, isReady: fxReady, loading: fxLoading, lastUpdated } = useFxConversion(
    currency,
    comparisonCurrency,
    { autoFetch: shouldShowFx }
  );

  // Format the lastUpdated timestamp for display
  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdated) return null;
    try {
      const date = new Date(lastUpdated);
      // Show relative time (e.g., "2 hours ago") or time if today
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      
      // If older than 24h, show the date
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  }, [lastUpdated]);

  // Convert amounts if FX is enabled
  const fxBalance = useMemo(() => {
    if (!shouldShowFx || !fxReady) return null;
    return convert(balance);
  }, [shouldShowFx, fxReady, convert, balance]);

  const fxIncome = useMemo(() => {
    if (!shouldShowFx || !fxReady) return null;
    return convert(income);
  }, [shouldShowFx, fxReady, convert, income]);

  const fxExpenses = useMemo(() => {
    if (!shouldShowFx || !fxReady) return null;
    return convert(expenses);
  }, [shouldShowFx, fxReady, convert, expenses]);

  useEffect(() => {
    let frame;
    const duration = 700;
    const startTime = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setAnimatedBalance(balance * progress);
      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      }
    };

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [balance]);

  const balanceColor = isPositive ? 'text-emerald-300' : 'text-rose-400';

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-tr from-[#0f2027] via-[#203a43] to-[#2c5364] p-6 text-white shadow-2xl">
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_65%)] opacity-80"
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black opacity-30" aria-hidden />
      <div className="relative z-10 space-y-6">
        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.4em] text-gray-300">
          💰 Net Balance
        </p>
        <div className="flex flex-col">
          <motion.h1
            className={`text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight ${balanceColor} drop-shadow-[0_5px_15px_rgba(0,0,0,0.45)]`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {formatCurrency(Math.abs(animatedBalance), currency)}
          </motion.h1>
          {/* FX Comparison Display */}
          {shouldShowFx && (
            <motion.div
              className="mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <p className={`text-lg sm:text-xl font-medium ${fxLoading ? 'animate-pulse' : ''} text-gray-300/80`}>
                {fxLoading ? '...' : fxBalance !== null ? `≈ ${formatCurrency(Math.abs(fxBalance), comparisonCurrency)}` : null}
              </p>
              {/* Last Updated Timestamp */}
              {!fxLoading && fxReady && formattedLastUpdated && (
                <p className="text-xs text-gray-400/60 mt-0.5">
                  Rate updated {formattedLastUpdated}
                </p>
              )}
            </motion.div>
          )}
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <motion.div
            className="relative z-10 rounded-2xl bg-white/10 p-4 shadow-lg shadow-black/40 backdrop-blur-xl border border-white/20"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-400/20">
                <TrendingUp className="h-4 w-4 text-emerald-300" />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wide text-gray-200">Income</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-emerald-300">
              {formatCurrency(income, currency)}
            </p>
            {/* FX Income */}
            {shouldShowFx && fxIncome !== null && (
              <p className="text-sm text-emerald-200/60 mt-0.5">
                ≈ {formatCurrency(fxIncome, comparisonCurrency)}
              </p>
            )}
          </motion.div>
          <motion.div
            className="relative z-10 rounded-2xl bg-white/10 p-4 shadow-lg shadow-black/40 backdrop-blur-xl border border-white/20"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-500/20">
                <TrendingDown className="h-4 w-4 text-rose-300" />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wide text-gray-200">Expenses</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-rose-300">
              {formatCurrency(expenses, currency)}
            </p>
            {/* FX Expenses */}
            {shouldShowFx && fxExpenses !== null && (
              <p className="text-sm text-rose-200/60 mt-0.5">
                ≈ {formatCurrency(fxExpenses, comparisonCurrency)}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
